import { useMemo, useState } from "react";
import { useBatchTaskImageUrls, useTaskImages } from "@dashboard/hooks/useTaskImageQuery";
import { TaskImagePreview } from "./TaskImagePreview";
import type { TaskImage, TaskWithOccurrences } from "@dashboard/api/task";
import { usePrintMode } from "@dashboard/contexts/printModeContext";

const VISIBLE_THUMB_COUNT = 3;

function Thumbnail({
    image,
    onClick,
    url,
    hasError,
}: {
    image: TaskImage;
    onClick: () => void;
    url?: string;
    hasError?: boolean;
}) {
    const isPrintMode = usePrintMode();

    return (
        <button
            onClick={onClick}
            className="w-12 h-12 rounded-[3px] border border-compendia-border overflow-hidden shrink-0 hover:ring-2 hover:ring-chameleon-green transition-all cursor-pointer"
            aria-label={image.fileName}
        >
            {hasError ? (
                <div className="w-full h-full bg-chameleon-pink/10 flex items-center justify-center text-xs text-chameleon-pink">
                    Failed
                </div>
            ) : url ? (
                <img
                    src={url}
                    alt={image.fileName}
                    loading={isPrintMode ? "eager" : "lazy"}
                    className="w-full h-full object-cover"
                />
            ) : (
                <div className="w-full h-full bg-compendia-greige-20 animate-pulse" />
            )}
        </button>
    );
}

function MoreCountBadge({ count, onClick }: { count: number; onClick: () => void }) {
    return (
        <button
            onClick={onClick}
            className="w-12 h-12 rounded-[3px] border border-compendia-border bg-compendia-greige-20 flex items-center justify-center text-sm font-medium text-compendia-foreground hover:bg-compendia-greige-40 transition-colors shrink-0 cursor-pointer"
            aria-label={`+${count}`}
        >
            +{count}
        </button>
    );
}

export function TaskImageThumbnails({
    taskId,
    task,
}: {
    taskId: number;
    task: TaskWithOccurrences;
}) {
    const { data } = useTaskImages(taskId);
    const isPrintMode = usePrintMode();
    const [selectedImageId, setSelectedImageId] = useState<number | null>(null);

    const images = data?.data ?? [];
    const imageIds = images.map((img) => img.id);

    const { data: batchUrls, isError } = useBatchTaskImageUrls(imageIds);

    const urlMap = useMemo(
        () => new Map(batchUrls?.map((item) => [item.id, item.url]) ?? []),
        [batchUrls],
    );

    if (images.length === 0) return null;

    const visibleImages = isPrintMode ? images : images.slice(0, VISIBLE_THUMB_COUNT);
    const hiddenCount = images.length - visibleImages.length;

    const initialIndex =
        selectedImageId !== null
            ? Math.max(
                  0,
                  images.findIndex((img) => img.id === selectedImageId),
              )
            : 0;

    const isSelectedImageValid =
        selectedImageId !== null && images.some((img) => img.id === selectedImageId);

    return (
        <>
            <div className="flex gap-2 flex-wrap mt-2">
                {visibleImages.map((image) => (
                    <Thumbnail
                        key={image.id}
                        image={image}
                        url={urlMap.get(image.id)}
                        hasError={isError}
                        onClick={() => setSelectedImageId(image.id)}
                    />
                ))}
                {hiddenCount > 0 && (
                    <MoreCountBadge
                        count={hiddenCount}
                        onClick={() => {
                            const firstHidden = images[VISIBLE_THUMB_COUNT];
                            setSelectedImageId(firstHidden.id);
                        }}
                    />
                )}
            </div>

            <TaskImagePreview
                key={selectedImageId ?? "closed"}
                images={images}
                imageUrls={urlMap}
                initialIndex={initialIndex}
                open={selectedImageId !== null && isSelectedImageValid}
                onOpenChange={(open) => {
                    if (!open) setSelectedImageId(null);
                }}
                task={task}
            />
        </>
    );
}
